// Copyright 2019 Sorama B.V.

import { Matrix } from "libraries/Math/Matrix";
import Basic3DTextureFragmentShader from "./../shaders/Basic3DTextureFragmentShader.shader";
import Basic3DVertexShader from "./../shaders/Basic3DVertexShader.shader";

export class DrawImageProgram {
    public static readonly MaxRenderResolution = 2048;
    private readonly contextGl: WebGLRenderingContext;
    private readonly context2D: CanvasRenderingContext2D;

    private program: WebGLProgram;
    private texture: WebGLTexture;

    private positionBuffer: WebGLBuffer;
    private texCoordBuffer: WebGLBuffer;

    private positionAttributeLocation: number;
    private texCoordAttributeLocation: number;
    private matrixLocation: WebGLUniformLocation;
    private coordinateMatrixLocation: WebGLUniformLocation;
    private textureLocation: WebGLUniformLocation;

    private matrices: number[][][] = [];

    constructor(
        private readonly canvasGlElement: HTMLCanvasElement,
        private readonly canvasElement: HTMLCanvasElement,
        canvasWidth: number,
        canvasHeight: number
    ) {
        this.canvasGlElement.width = Math.round(canvasWidth);
        this.canvasGlElement.height = Math.round(canvasHeight);

        // Get webgl context
        this.contextGl = this.canvasGlElement.getContext("webgl", { preserveDrawingBuffer: true });
        this.context2D = this.canvasElement.getContext("2d");

        this.initialize();
    }

    public reset(canvasWidth: number, canvasHeight: number) {
        if (this.canvasGlElement.width !== Math.round(canvasWidth)) {
            this.canvasGlElement.width = Math.round(canvasWidth);
        }

        if (this.canvasGlElement.height !== Math.round(canvasHeight)) {
            this.canvasGlElement.height = Math.round(canvasHeight);
        }

        // Also resize the contex viewport
        this.contextGl.viewport(0, 0, canvasWidth, canvasHeight);

        // Clear all transforms
        this.matrices.splice(0, this.matrices.length);
    }

    public pushMatrix(matrix: number[][]): DrawImageProgram {
        this.matrices.push(matrix);
        return this;
    }

    public popMatrix(): DrawImageProgram {
        this.matrices.pop();
        return this;
    }

    public drawImage(img: string, dx = 0, dy = 0, width = 1, height = 1): Promise<string> {
        // Initialize the texture
        return this.initializeTexture(img).then(() => {
            this.updateBuffers(dx, dy, width, height);
            this.contextGl.clearColor(0, 0, 0, 1);
            this.contextGl.clear(WebGLRenderingContext.COLOR_BUFFER_BIT);

            this.contextGl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.texture);
            this.contextGl.useProgram(this.program);

            this.contextGl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.positionBuffer);
            this.contextGl.enableVertexAttribArray(this.positionAttributeLocation);
            this.contextGl.vertexAttribPointer(
                this.positionAttributeLocation,
                2,
                WebGLRenderingContext.FLOAT,
                false,
                0,
                0
            );
            this.contextGl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.texCoordBuffer);
            this.contextGl.enableVertexAttribArray(this.texCoordAttributeLocation);
            this.contextGl.vertexAttribPointer(
                this.texCoordAttributeLocation,
                2,
                WebGLRenderingContext.FLOAT,
                false,
                0,
                0
            );

            const matrix = this.getTotalTransform();
            this.contextGl.uniformMatrix4fv(this.matrixLocation, false, matrix);
            this.contextGl.uniform1i(this.textureLocation, 0);

            this.contextGl.drawArrays(WebGLRenderingContext.TRIANGLE_STRIP, 0, 4);

            return this.canvasGlElement.toDataURL();
        });
    }

    private initializeTexture(img: string): Promise<void> {
        this.texture = this.contextGl.createTexture();
        this.contextGl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.texture);
        this.contextGl.texParameteri(
            WebGLRenderingContext.TEXTURE_2D,
            WebGLRenderingContext.TEXTURE_WRAP_S,
            WebGLRenderingContext.CLAMP_TO_EDGE
        );
        this.contextGl.texParameteri(
            WebGLRenderingContext.TEXTURE_2D,
            WebGLRenderingContext.TEXTURE_WRAP_T,
            WebGLRenderingContext.CLAMP_TO_EDGE
        );
        this.contextGl.texParameteri(
            WebGLRenderingContext.TEXTURE_2D,
            WebGLRenderingContext.TEXTURE_MIN_FILTER,
            WebGLRenderingContext.LINEAR
        );
        this.contextGl.texParameteri(
            WebGLRenderingContext.TEXTURE_2D,
            WebGLRenderingContext.TEXTURE_MAG_FILTER,
            WebGLRenderingContext.LINEAR
        );

        return new Promise((resolve, reject) => {
            // Load image
            const image = new Image();
            image.addEventListener("load", () => {
                const smallImage =
                    image.width < DrawImageProgram.MaxRenderResolution &&
                    image.height < DrawImageProgram.MaxRenderResolution;

                if (!smallImage) {
                    // Resize 2d canvas to an aspect ratio that fits the image
                    const scale = Math.min(
                        Math.min(
                            DrawImageProgram.MaxRenderResolution / image.width,
                            DrawImageProgram.MaxRenderResolution / image.height
                        ),
                        1
                    );
                    const cw = Math.round(scale * image.width);
                    const ch = Math.round(scale * image.height);
                    this.canvasElement.width = cw;
                    this.canvasElement.height = ch;
                    this.context2D.drawImage(image, 0, 0, image.width, image.height, 0, 0, cw, ch);
                }

                // Bind texture in the gl context
                this.contextGl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.texture);
                this.contextGl.texImage2D(
                    WebGLRenderingContext.TEXTURE_2D,
                    0,
                    WebGLRenderingContext.RGB,
                    WebGLRenderingContext.RGB,
                    WebGLRenderingContext.UNSIGNED_BYTE,
                    smallImage ? image : this.canvasElement
                );

                resolve();
            });

            image.addEventListener("error", () => {
                reject(new Error("Unable to load image"));
            });

            image.src = img;
        });
    }

    private updateBuffers(dx: number, dy: number, width: number, height: number): void {
        this.contextGl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.positionBuffer);
        const vertexData = new Float32Array(4 * 5);
        vertexData[0] = dx;
        vertexData[1] = dy;
        vertexData[2] = dx + width;
        vertexData[3] = dy;
        vertexData[4] = dx;
        vertexData[5] = dy + height;
        vertexData[6] = dx + width;
        vertexData[7] = dy + height;

        this.contextGl.bufferData(WebGLRenderingContext.ARRAY_BUFFER, vertexData, WebGLRenderingContext.DYNAMIC_DRAW);
    }

    private initialize(): void {
        // Generate coordinate buffers
        this.positionBuffer = this.contextGl.createBuffer();
        this.texCoordBuffer = this.contextGl.createBuffer();

        const texelData = new Float32Array(4 * 2);
        texelData[0] = 0;
        texelData[1] = 0;
        texelData[2] = 1;
        texelData[3] = 0;
        texelData[4] = 0;
        texelData[5] = 1;
        texelData[6] = 1;
        texelData[7] = 1;

        this.contextGl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.texCoordBuffer);
        this.contextGl.bufferData(WebGLRenderingContext.ARRAY_BUFFER, texelData, WebGLRenderingContext.DYNAMIC_DRAW);

        // Compile shaders
        const vertexShader = this.contextGl.createShader(WebGLRenderingContext.VERTEX_SHADER);
        const fragmentShader = this.contextGl.createShader(WebGLRenderingContext.FRAGMENT_SHADER);

        this.contextGl.shaderSource(vertexShader, Basic3DVertexShader);
        this.contextGl.compileShader(vertexShader);
        if (!this.contextGl.getShaderParameter(vertexShader, WebGLRenderingContext.COMPILE_STATUS)) {
            throw new Error(this.contextGl.getShaderInfoLog(vertexShader));
        }

        // Compile shaders
        this.contextGl.shaderSource(fragmentShader, Basic3DTextureFragmentShader);
        this.contextGl.compileShader(fragmentShader);
        if (!this.contextGl.getShaderParameter(fragmentShader, WebGLRenderingContext.COMPILE_STATUS)) {
            throw new Error(this.contextGl.getShaderInfoLog(fragmentShader));
        }

        // Create program
        this.program = this.contextGl.createProgram();
        this.contextGl.attachShader(this.program, vertexShader);
        this.contextGl.attachShader(this.program, fragmentShader);
        this.contextGl.linkProgram(this.program);

        if (!this.contextGl.getProgramParameter(this.program, WebGLRenderingContext.LINK_STATUS)) {
            throw new Error(this.contextGl.getProgramInfoLog(this.program));
        }

        this.contextGl.useProgram(this.program);
        this.positionAttributeLocation = this.contextGl.getAttribLocation(this.program, "position");
        this.texCoordAttributeLocation = this.contextGl.getAttribLocation(this.program, "texcoords");

        this.matrixLocation = this.contextGl.getUniformLocation(this.program, "matrix");
        this.coordinateMatrixLocation = this.contextGl.getUniformLocation(this.program, "coordinateMatrix");
        this.textureLocation = this.contextGl.getUniformLocation(this.program, "texture");

        const coordinateMatrix = new Float32Array(16);
        coordinateMatrix[0] = 2;
        coordinateMatrix[1] = 0;
        coordinateMatrix[2] = 0;
        coordinateMatrix[3] = 0;
        coordinateMatrix[4] = 0;
        coordinateMatrix[5] = -2;
        coordinateMatrix[6] = 0;
        coordinateMatrix[7] = 0;
        coordinateMatrix[8] = 0;
        coordinateMatrix[9] = 0;
        coordinateMatrix[10] = 2;
        coordinateMatrix[11] = 0;
        coordinateMatrix[12] = -1;
        coordinateMatrix[13] = 1;
        coordinateMatrix[14] = 0;
        coordinateMatrix[15] = 1;
        this.contextGl.uniformMatrix4fv(this.coordinateMatrixLocation, false, coordinateMatrix);
    }

    private getTotalTransform(): Float32Array {
        if (this.matrices.length === 0) {
            return Matrix.toFloat32Array(Matrix.identity(4));
        }
        if (this.matrices.length === 1) {
            return Matrix.toFloat32Array(this.matrices[0]);
        } else {
            // Multiply matrices
            let matrix = this.matrices[0];
            for (let i = 1; i < this.matrices.length; i++) {
                matrix = Matrix.multiply(this.matrices[i], matrix);
            }

            return Matrix.toFloat32Array(matrix);
        }
    }
}
